// src/components/PopupForm.js
import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import "./PopUpForm.css";
//import { initializeApp } from "firebase/app";
import LoginForm from "../Login";
import { imageStorage } from "../../firebase.js";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { ENDPOINT_PATH } from "../../config";

/*
const firebaseConfig = {
  apiKey: "AIzaSyB8JTEnZU3vmHoN_Lcv9x5gJfA2d0TZ3CU",
  authDomain: "mycustomframe-eeb5c.firebaseapp.com",
  projectId: "mycustomframe-eeb5c",
  storageBucket: "mycustomframe-eeb5c.appspot.com",
  messagingSenderId: "457371732882",
  appId: "1:457371732882:web:11c1e14e2964df98db0f4b",
  measurementId: "G-LQ367VJ0GM"
};

//const app = initializeApp(firebaseConfig);
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// ✅ keep these lines
export const auth = getAuth(app);
export const storage = getStorage(app);
*/
const API_URL = `${ENDPOINT_PATH}/api/artist-application`;

export const uploadFileAndGetURL = (file, path) =>
  new Promise((resolve, reject) => {
    const storageRef = ref(imageStorage, `${path}/${Date.now()}_${file.name}`);
    const task = uploadBytesResumable(storageRef, file);

    task.on(
      "state_changed",
      null,
      (err) => reject(err),
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve(url);
      }
    );
  });

const PopupForm = ({ buttonLabel = "Open Form", onSubmit, isLoggedIn, showButton = true, onLoginSuccess }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [checkingApplication, setCheckingApplication] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    socialLinks: "",
    artistBio: "",
    artistPhotoURL: "",
    resaleCertificate: ""
  });

  const [uploading, setUploading] = useState({ photo: false, cert: false });
  //const [error, setError] = useState(null);
  const [wasWaitingForLogin, setWasWaitingForLogin] = useState(false);

  const outerStoredUser = JSON.parse(localStorage.getItem('user') || 'null');
  const accountEmailDisplay = outerStoredUser?.email || "";

  // Keep popup open when user logs in
  useEffect(() => {
    // Track if we're waiting for login (popup open but user not logged in)
    if (showPopup && !isLoggedIn) {
      setWasWaitingForLogin(true);
    }
    
    // When user logs in and we were waiting, ensure popup stays open
    if (isLoggedIn && wasWaitingForLogin && !showPopup) {
      setShowPopup(true);
      setWasWaitingForLogin(false);
    } else if (isLoggedIn && wasWaitingForLogin) {
      // User logged in and popup is already open - reset flag
      setWasWaitingForLogin(false);
    }
  }, [isLoggedIn, showPopup, wasWaitingForLogin]);

  // Check if user already has a pending/approved application when popup opens
  useEffect(() => {
    if (!showPopup || !isLoggedIn) return;
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user?.uid) return;
    let cancelled = false;
    const check = async () => {
      setCheckingApplication(true);
      try {
        const res = await fetch(`${ENDPOINT_PATH}/api/artist-application/has-application?uid=${encodeURIComponent(user.uid)}`);
        if (!cancelled && res.ok) {
          const data = await res.json();
          setAlreadyApplied(data.hasApplication === true);
        }
      } catch {
        // If check fails, allow the form to show
      } finally {
        if (!cancelled) setCheckingApplication(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [showPopup, isLoggedIn]);

  //sets the URL into formData
  const handleFileUpload = async (e, field, path) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setError(null);
      setUploading((u) => ({ ...u, [field]: true }));
      const url = await uploadFileAndGetURL(file, path);
      setFormData((prev) => ({ ...prev, [field]: url }));   //stores only URL
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [field]: false }));
    }
  };

  // Text inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  //frontend validation
  const validate = () => {
    if (!formData.name.trim()) return "Name is required.";
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountEmailDisplay);
    if (!emailOk) return "Please enter a valid email address.";
    if (!formData.artistBio.trim()) return "Short bio is required.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    console.log("Submitting form…", formData);

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    // Get the logged-in user's email from localStorage
    const storedUser = JSON.parse(localStorage.getItem('user'));
    const accountEmail = storedUser?.email || null;

    if (!accountEmail) {
      setError("You must be logged in to submit an application.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        name: formData.name,
        email: accountEmail,
        phoneNumber: formData.phoneNumber,
        socialLinks: formData.socialLinks,
        artistBio: formData.artistBio,
        artistPhotoURL: formData.artistPhotoURL,
        resaleCertificate: formData.resaleCertificate,
        accountEmail: accountEmail, // Email of the logged-in account
      };

      //send JSON object to API 
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        let msg = "Failed to submit application.";
        try {
          const data = JSON.parse(text);
          msg = data?.error || msg;
        } catch {
          if (text) msg = text;
        }
        throw new Error(msg);
      }

      const data = await res.json();
      onSubmit?.(data);
      setSuccess("Your application has been submitted!");

      setTimeout(() => {
        setShowPopup(false);
        setSuccess("");
      }, 3000); // 3 seconds delay before closing

      setFormData({
        artistBio: "",
        artistPhotoURL: "",
        email: "",
        name: "",
        phoneNumber: "",
        resaleCertificate: "",
        socialLinks: "",
      });
      //setShowPopup(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  console.log("[PopupForm] isLoggedIn prop:", isLoggedIn);

  if (!showButton) {
    return null;
  }

  return (
    <div style={{ display: 'inline-block', maxWidth: '100%' }}>
      {/* Trigger Button */}
      <button
        onClick={() => setShowPopup(true)}
        className="modalButton triggerButton"      >
        {buttonLabel}
      </button>

      {/* Modal Form */}
      {showPopup && (
        <div className="modalBackground">
          <div className="modalContainer">
          {isLoggedIn ? ( 
            <>
    
            <h2 className="text-xl font-semibold text-center mb-4">Artist Application</h2>

            {checkingApplication ? (
              <p className="text-center text-gray-500">Loading...</p>
            ) : alreadyApplied ? (
              <div className="text-center" style={{ padding: '20px' }}>
                <p style={{ marginBottom: '16px', color: '#555' }}>
                  You have already submitted an application that is currently being reviewed or has been approved.
                </p>
                <button
                  type="button"
                  className="modalButton cancelButton"
                  onClick={() => setShowPopup(false)}
                >
                  Close
                </button>
              </div>
            ) : (

            <form onSubmit={handleSubmit} >
              {/* Form Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-6 items-center">

                {/* Name */}
                <label className="text-gray-700 font-medium md:text-right">Name:</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />

                {/* Email */}
                <label className="text-gray-700 font-medium md:text-right">Email:</label>
                <input
                  type="email"
                  name="email"
                  value={accountEmailDisplay}
                  readOnly
                  disabled
                  title="Your email is tied to your account"
                  className="border border-gray-300 rounded-md p-2 w-full bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />

                {/* Phone Number */}
                <label className="text-gray-700 font-medium md:text-right">Phone Number:</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  required
                />

                {/* Profile Photo */}
                <label className="text-gray-700 font-medium md:text-right">Profile Photo:</label>
                <input
                  type="file"
                  name="artistPhotoURL"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, "artistPhotoURL", "artists/photos")}
                  className="border border-gray-300 rounded-md p-2 w-full bg-white focus:outline-none"
                />

                {/* Social Media / Portfolio Links */}
                <label className="text-gray-700 font-medium md:text-right">Social Media / Portfolio Links:</label>
                <input
                  type="text"
                  name="socialLinks"
                  value={formData.socialLinks}
                  onChange={handleChange}
                  placeholder="e.g. Instagram, Behance, or website"
                  className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                />

                {/* Short Bio */}
                <label className="text-gray-700 font-medium md:text-right self-start mt-1">Short Bio:</label>
                <textarea
                  name="artistBio"
                  value={formData.artistBio}
                  onChange={handleChange}
                  placeholder="Tell us a little about yourself..."
                  rows="2"
                  className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                ></textarea>

                {/* Resale Info */}
                <label className="text-gray-700 font-medium md:text-right">Resale Certificate:</label>
                <input
                  type="file"
                  name="resaleCertificate"
                  accept="application/pdf,image/*"
                  onChange={(e) => handleFileUpload(e, "resaleCertificate", "artists/resale-certificates")}
                  className="border border-gray-300 rounded-md p-2 w-full bg-white focus:outline-none"
                />
              </div>

              {/* Success Message*/}
            {success && (
              <div className="text-green-500 text-center font-medium mb-4">
                {success}
              </div>
            )}

              <div className="buttonGroup">
                {/* Submit Button */}
                <button
                  type="submit"
                  className="modalButton submitButton"
                >
                  Submit Application
                </button>

                {/* Close Button */}
                <button
                  type="button"
                  className="modalButton cancelButton"
                  onClick={() => setShowPopup(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
            )}
            </>) : (

          <LoginForm handleClose={(user) => {
            // If user is null/undefined, they clicked X button - close the popup
            // If user exists, they just logged in - keep popup open so form can show
            if (!user) {
              setShowPopup(false);
            } else {
              onLoginSuccess?.();
            }
            // If user exists, do nothing - popup stays open and form will show
            // The isLoggedIn prop change will trigger showing the form
          }} />

            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupForm;